import {
    ArrowPathIcon,
    PlusCircleIcon,
    XMarkIcon,
    CheckIcon,
    PlusIcon,
    BanknotesIcon,
  } from "@heroicons/react/24/solid";
  import { useState, useEffect } from "react";
  import { useNavigate } from "react-router-dom";
  import { TextInput, ToggleInput, CheckboxInput, SelectInput } from "../components/Inputs";
  import { PageHeader } from "../components/Layouts";
  import { ActiveFilter, PrimaryTable } from "../components/Tables";
  import { useDevManager, useLoaderStore, useUserStore } from "../context";
  import { useAlert, useApi } from "../hooks";
  import constants from "../utils/constants";
  import dateHelpers from "../utils/dateHelpers";
  import { convertToLabelValueList, formatCurrency, formatPhoneNumber, isSystemAdmin, resolveSystemTenantId, switchSortDirection } from "../utils/helpers";
  import { PrimaryButton } from "../components/Buttons";
  import { Popover } from "@headlessui/react";
  import { RefundConfirmationModal } from "../components/Modals";
  import { ReceiptRefundIcon } from "@heroicons/react/24/outline";
  
  const EMPTY_FILTERS = {
    activeOnly: false,
  };
  
  export default function OwnedProducts() {
    const navigate = useNavigate();
    const alert = useAlert();
    const { post, fetch, del } = useApi();
    const { setShowLoader } = useLoaderStore();
    const { currentUser, activeSystemTenant, systemTenants } = useUserStore();
    const { IS_DEV } = useDevManager();
    const [activeFilters, setActiveFilters] = useState({
      ...EMPTY_FILTERS,
      activeOnly: true,
    });
    const [stagedFilters, setStagedFilters] = useState(activeFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortDirection, setSortDirection] = useState(
      constants.SORT_DIRECTIONS.DESCENDING
    );
    const [sortField, setSortField] = useState("effectiveAt");
    // SPR-014: refund an owned product belonging to one of the caller's OWN system tenants. This is the
    // account-admin capability (RefundSystemTenantOwnedProduct) -- distinct from the sysadmin-only refund of what
    // an account pays Margot, which lives on AccountProfile.
    const [refundTarget, setRefundTarget] = useState(null);   // the row being refunded
    const [refundPreview, setRefundPreview] = useState(null);
    const [refundPreviewLoading, setRefundPreviewLoading] = useState(false);
    const [displayCount, setDisplayCount] = useState(
      constants.DISPLAY_PER_PAGE ?? 25
    );
    const [data, setData] = useState({ list: [], totalCount: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [systemTenantList, setSystemTenantList] = useState();
    const STATUS_LIST = Object.values(constants.PRODUCT_STATUS);
  
    useEffect(() => {
      getData();
      getSystemTenants();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, displayCount, activeFilters, sortDirection, sortField, activeSystemTenant]);
  
    useEffect(() => {
      setStagedFilters(activeFilters);
      setCurrentPage(1);
    }, [activeFilters]);
  
    // SPR-014: open the refund modal for a row and load its preview, so the operator sees what is actually
    // refundable rather than typing a pennies amount blind. Preview failure is non-fatal (the modal warns).
    const openRefundModal = (row) => {
      setRefundTarget(row);
      setRefundPreview(null);
      if (!row?.id) return;
      setRefundPreviewLoading(true);
      fetch(`admin/PreviewRefundSystemTenantOwnedProduct/${row.id}`)
        .then((res) => setRefundPreview(res))
        .catch(() => setRefundPreview(null))
        .finally(() => setRefundPreviewLoading(false));
    };

    const refundOwnedProduct = (refundAmount, refundReason, keepMembershipActive) => {
      if (!refundTarget?.id) return;
      setShowLoader(true);
      // NOTE: the server guards and refunds the ROUTE id, so OwnedProductId in the body is informational only --
      // it cannot be used to redirect the refund at another subscription.
      del(`admin/RefundSystemTenantOwnedProduct/${refundTarget.id}`, {
        OwnedProductId: refundTarget.id,
        PartialRefundAmount: refundAmount,
        PartialRefund: refundAmount < (refundPreview?.paidPrice ?? refundTarget.price),
        RefundReason: refundReason || null,
        NotifyTenantUsers: true,
        KeepMembershipActive: !!keepMembershipActive,
      })
        .then(() => {
          alert(
            keepMembershipActive ? "Refunded — membership kept active" : "Refunded",
            keepMembershipActive
              ? "The subscription keeps its end date and will still renew."
              : "",
            "success"
          );
          getData();
        })
        .catch((err) => {
          alert("Server error", err?.Message || err?.data?.message || "Error processing refund", "error");
        })
        .finally(() => {
          setRefundTarget(null);
          setShowLoader(false);
        });
    };

    const getData = () => {
      setIsLoading(true);
      fetch("admin/ownedProductList", {
        pageNumber: currentPage,
        pageSize: displayCount,
        sortDirection,
        sortField,
        isExact: false,
        systemTenantId: activeSystemTenant?.id,//resolveSystemTenantId(currentUser, activeSystemTenant),
        ...activeFilters,
      })
        .then((res) => {
          setData(res);
        })
        .catch((err) => {
          alert("Server error", "Could not retrieve products", "error");
          console.error(err);
        })
        .finally(() => setIsLoading(false));
    };

    const getSystemTenants = () => {
      if (Array.isArray(systemTenants) && systemTenants.length) {
        setSystemTenantList(convertToLabelValueList(systemTenants));
        return;
      }
      setIsLoading(true);
      fetch(`admin/GetSystemTenantsSimple`, {
        // pageNumber: currentPage,
        // pageSize: displayCount,
        sortDirection: 'asc',
        sortField: 'name',
        isExact: false,
        systemTenantId: resolveSystemTenantId(currentUser, activeSystemTenant),
      })
        .then((res) => {
          if (Array.isArray(res))
            setSystemTenantList(convertToLabelValueList(res));
        })
        .catch((err) => {
          alert("Server error", "Could not retrieve products", "error");
          console.error(err);
        })
        .finally(() => setIsLoading(false));
    };
  
    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(switchSortDirection(sortDirection));
      } else {
        setSortField(field);
      }
    };
    
  
    return (
      <div>
        {/* Header */}
        <div className="flex justify-between">
          <PageHeader title="Manage Owned Products" />
        </div>
  
        {/* Filter section */}
        <OwnedProductFilterSection
          stagedFilters={stagedFilters}
          setStagedFilters={setStagedFilters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          systemTenantList={systemTenantList}
          statusList={STATUS_LIST}
        />
  
        {/* Table */}
        <PrimaryTable
          isLoading={isLoading}
          totalCount={data?.totalCount}
          displayCount={displayCount}
          currentPage={currentPage}
          setDisplayCount={setDisplayCount}
          setCurrentPage={setCurrentPage}
          header={
            <PrimaryTable.Header>
              {IS_DEV &&
              <PrimaryTable.HeaderColumn
                display="Id"
                onSort={() => handleSort("id")}
                isSortingBy={sortField === "id"}
                sortDirection={sortDirection}
              />}
              <PrimaryTable.HeaderColumn
                display="Price"
                onSort={() => handleSort("price")}
                isSortingBy={sortField === "price"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Name"
                onSort={() => handleSort("name")}
                isSortingBy={sortField === "name"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Effective At"
                onSort={() => handleSort("effectiveAt")}
                isSortingBy={sortField === "effectiveAt"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Expires At"
                onSort={() => handleSort("expiresAt")}
                isSortingBy={sortField === "expiresAt"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Status"
                onSort={() => handleSort("status")}
                isSortingBy={sortField === "status"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Product"
              />
              <PrimaryTable.HeaderColumn display="Payments" />
              <PrimaryTable.HeaderColumn display="Refund" />
              {/* <PrimaryTable.HeaderColumn display="" /> */}
            </PrimaryTable.Header>
          }
        >
          {data &&
            data.list &&
            data.list.map((x, i) => (
              <PrimaryTable.Row
                key={`${x.id}-${i}`}
                index={i}
                // onClick={(e) => {
                //   return false;
                //   // if (!x.deactivatedAt) {
                //   //   navigate(`/ownedProducts/${x.id}`);
                //   // }
                // }}
              >
                {IS_DEV &&
                (<PrimaryTable.Cell>
                  {x.id}
                </PrimaryTable.Cell>)}
                <PrimaryTable.Cell>
                  {formatCurrency(x.price, true)}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.name}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {dateHelpers.localDateTime(x.effectiveAt, dateHelpers.YMDHMSA)}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                {dateHelpers.localDateTime(x.expiresAt, dateHelpers.YMDHMSA)}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.statusName}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  {x.productName}
                </PrimaryTable.Cell>
                <PrimaryTable.Cell>
                  <a className="flex place-content-center" href={`/payments?ownedProductId=${x.id}`} target="_blank" rel="noopener noreferrer">
                    <BanknotesIcon
                      title="View payments"
                      className="w-6 mx-1 text-white bg-green-700 rounded-md p-0.5 cursor-pointer"
                      onClick={(e) => {
                          e.stopPropagation();
                        //   toggleActiveStatus(x);
                      }}
                    />
                  </a>
                </PrimaryTable.Cell>
                {/* SPR-014: refund this subscriber's payment. Only offered when something was actually charged --
                    a $0 row has nothing to refund and the server would reject it ("Invalid refund amount"). */}
                <PrimaryTable.Cell>
                  <div className="flex place-content-center">
                    {x.price > 0 ? (
                      <ReceiptRefundIcon
                        title="Refund this payment"
                        className="w-6 mx-1 text-white bg-fuchsia-700 hover:bg-fuchsia-900 rounded-md p-0.5 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRefundModal(x);
                        }}
                      />
                    ) : null}
                  </div>
                </PrimaryTable.Cell>
                {/* <PrimaryTable.Cell>
                  <div className="flex place-content-center">
                    {x.deactivatedAt ? (
                        <ArrowPathIcon
                            title="Activate"
                            className="w-6 mx-1 text-white bg-green-500 rounded-md p-0.5 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                              //   toggleActiveStatus(x);
                            }}
                        />
                    ) : (
                        <XMarkIcon
                            title="Deactivate"
                            className="w-6 mx-1 text-white bg-delete rounded-md p-0.5 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                              //   toggleActiveStatus(x);
                            }}
                        />
                    )}
                  </div>
                </PrimaryTable.Cell> */}
              </PrimaryTable.Row>
            ))}
        </PrimaryTable>

        {/* SPR-014: refund a subscriber's payment. Reuses the same modal as the sysadmin flow, so the preview,
            the eligible-amount bound and the partial-refund cancel-vs-keep choice all behave identically. */}
        <RefundConfirmationModal
          modalOpen={!!refundTarget}
          setModalOpen={(open) => { if (!open) setRefundTarget(null); }}
          title={<span className="text-lg font-semibold">Refund payment</span>}
          message={refundTarget
            ? `${refundTarget.name ?? refundTarget.productName ?? "Subscription"} — charged ${formatCurrency(refundTarget.price ?? 0, true)}`
            : ""}
          textToMatch={currentUser?.username}
          confirmButtonText={"CONFIRM REFUND"}
          productPrice={refundTarget?.price}
          preview={refundPreview}
          previewLoading={refundPreviewLoading}
          onConfirm={refundOwnedProduct}
        />
      </div>
    );
  }
  
  const OwnedProductFilterSection = ({
    stagedFilters,
    setStagedFilters,
    activeFilters,
    setActiveFilters,
    systemTenantList,
    statusList
  }) => {
    return (
      <div className="relative mb-4 rounded-xs shadow-sm bg-white p-4">
        <Popover as="div" className="relative">
          {({ open }) => (
            <>
              <Popover.Button className="flex items-center text-sm bg-gray-100 px-3 py-2 rounded-sm hover:bg-gray-200 relative focus:outline-none">
                <PlusIcon className="w-4 mr-1" />
                FILTERS
              </Popover.Button>
              <Popover.Panel
                as="div"
                className="absolute grid gap-y-4 z-50 w-full sm:w-1/3 origin-bottom-right bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none mt-1"
              >
                <div>
                  <CheckboxInput
                    name="active-only"
                    label="Show active only"
                    checked={stagedFilters.activeOnly === true}
                    onChange={() =>
                      setStagedFilters({
                        ...stagedFilters,
                        activeOnly: stagedFilters.activeOnly ? false : true,
                      })
                    }
                  />
                </div>
                <div>
                  <CheckboxInput
                    name="is-effective"
                    label="Show only owned prodcuts that are effective now"
                    checked={stagedFilters.isEffectiveNow === true}
                    onChange={() =>
                      setStagedFilters({
                        ...stagedFilters,
                        isEffectiveNow: stagedFilters.isEffectiveNow ? false : true,
                      })
                    }
                  />
                </div>
                <div>
                  <TextInput
                    label="Name"
                    placeholder="Enter Name" to filter by
                    value={stagedFilters.name}
                    onChange={(e) =>
                      setStagedFilters({
                        ...stagedFilters,
                        name: e.target.value || undefined,
                      })
                    }
                  />
                </div>
                <div>
                  <SelectInput
                    label="Product Type"
                    options={constants.PRODUCT_TYPES}
                    isSearchable
                    value={constants.PRODUCT_TYPES.find(x => x.value === stagedFilters.productType)}
                    onChange={x => {
                      setStagedFilters({
                        ...stagedFilters,
                        productType: x.value || undefined,
                      });
                    }}
                    onClear={() => {
                      setStagedFilters({
                        ...stagedFilters,
                        productType: undefined,
                      });
                    }}
                  />
                </div>
                <div className={Array.isArray(systemTenantList) ? "" : "hidden"}>
                  <SelectInput
                    label="System Tenant"
                    options={systemTenantList}
                    isSearchable
                    value={systemTenantList?.find(x => x.value === stagedFilters.systemTenantId)}
                    onChange={x => {
                      setStagedFilters({
                        ...stagedFilters,
                        systemTenantId: x.value || undefined,
                      });
                    }}
                    onClear={() => {
                      setStagedFilters({
                        ...stagedFilters,
                        systemTenantId: undefined,
                      });
                    }}
                  />
                </div>
                <div className={Array.isArray(statusList) ? "" : "hidden"}>
                  <SelectInput
                    label="Status"
                    options={statusList}
                    isSearchable
                    value={statusList?.find(x => x.value === stagedFilters.status)}
                    onChange={x => {
                      setStagedFilters({
                        ...stagedFilters,
                        status: x.value || undefined,
                      });
                    }}
                    onClear={() => {
                      setStagedFilters({
                        ...stagedFilters,
                        status: undefined,
                      });
                    }}
                  />
                </div>
                {/* Add filters here */}
                <PrimaryButton
                  text="Apply filters"
                  onClick={() => setActiveFilters(stagedFilters)}
                />
              </Popover.Panel>
            </>
          )}
        </Popover>
  
        {/* Selected filters */}
        {Object.values(activeFilters).find(
          (val) => val !== undefined && val !== false
        ) ? (
          <div className="flex items-center space-x-2 pt-6 text-sm">
            <div>Selected:</div>
            <ActiveFilter
              display="Show active only"
              value={
                activeFilters.activeOnly ? (
                  <CheckIcon className="w-4 text-green-600" />
                ) : undefined
              }
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  activeOnly: false,
                })
              }
            />  
            <ActiveFilter
              display="Show only owned prodcuts that are effective now"
              value={
                activeFilters.isEffectiveNow ? (
                  <CheckIcon className="w-4 text-green-600" />
                ) : undefined
              }
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  isEffectiveNow: false,
                })
              }
            />  
            <ActiveFilter
              display="Name"
              value={activeFilters.name}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  name: undefined,
                })
              }
            />
            <ActiveFilter
              display="Product Type"
              value={constants.PRODUCT_TYPES.find(x => !!x.value && x.value === activeFilters.productType)?.label}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  productType: undefined,
                })
              }
            />
            <ActiveFilter
              display="System Tenant"
              value={systemTenantList?.find(x => x.value === activeFilters.systemTenantId)?.label}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  systemTenantId: undefined,
                })
              }
            />
            <ActiveFilter
              display="Status"
              value={statusList?.find(x => x.value === activeFilters.status)?.label}
              onClear={() =>
                setActiveFilters({
                  ...activeFilters,
                  status: undefined,
                })
              }
            />
  
            <div
              className="cursor-pointer bg-gray-100 px-3 py-2 text-xs hover:bg-gray-200"
              onClick={() => {
                setActiveFilters({ ...EMPTY_FILTERS });
              }}
            >
              Clear all
            </div>
          </div>
        ) : (
          <div className="pt-6 text-sm text-gray-400">No active filters</div>
        )}
      </div>
    );
  };
  